import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney } from '@teentack/shared';
import { useShortlist, useToggleShortlist } from '@/features/shortlist/use-shortlist';
import { TeenShell } from '@/features/teen/TeenShell';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorNote, InfoNote } from '@/components/Form';
import { Button } from '@/components/ui/Button';
import { ArrowRightIcon, MapPinIcon, SparkleIcon, WalletIcon } from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';

/**
 * The shortlist. [Teen step 8]
 *
 * Its own page rather than a tab, because it is where a teen comes back to —
 * "the ones I liked" is a different intent from "the ones I sent".
 */
export function SavedJobsPage() {
  const shortlist = useShortlist({ pageSize: 50 });
  const toggle = useToggleShortlist();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function remove(jobId: string, title: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await toggle.mutateAsync({ jobId, saved: true });
      setNotice(`${title} removed from your saved jobs.`);
    } catch (caught) {
      setError(presentApiError(caught, 'Could not update your saved jobs').message);
    }
  }

  const page = shortlist.data;

  return (
    <TeenShell
      title="Saved jobs"
      description="Jobs you kept for later. Nothing here has been applied for yet."
    >
      <div className="mb-6 space-y-3">
        <ErrorNote message={error} />
        {notice && <InfoNote>{notice}</InfoNote>}
      </div>

      {shortlist.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      )}

      {page && page.items.length === 0 && (
        <EmptyState
          icon={<SparkleIcon className="size-6" />}
          title="Nothing saved yet"
          action={
            <Link
              to="/jobs"
              className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-700"
            >
              Browse opportunities
              <ArrowRightIcon className="size-4" />
            </Link>
          }
        >
          Tap Save on any job and it lands here, so you can think about it and come back.
        </EmptyState>
      )}

      {page && page.items.length > 0 && (
        <ul className="grid gap-4 lg:grid-cols-2">
          {page.items.map((saved) => (
            <li
              key={saved.id}
              className="rounded-2xl border border-line bg-white p-5 shadow-soft transition hover:border-brand-200"
            >
              <Link to={`/jobs/${saved.job.id}`} className="block">
                <h2 className="text-base font-bold text-ink hover:underline">{saved.job.title}</h2>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  <span className="font-semibold text-slate-600">{saved.job.posterName}</span>
                  {saved.job.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPinIcon className="size-3.5 text-slate-400" />
                      {saved.job.location}
                    </span>
                  )}
                </p>
              </Link>

              {saved.savedByName && (
                <p className="mt-3 rounded-xl bg-brand-50 px-3.5 py-2 text-xs font-medium text-brand-800">
                  Shared with you by {saved.savedByName}
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-ink">
                  <WalletIcon className="size-4 text-safe-600" />
                  {formatMoney(saved.job.pay.amountMinor, saved.job.pay.currency)}
                </span>
                <div className="flex gap-2">
                  <Link
                    to={`/jobs/${saved.job.id}`}
                    className="rounded-xl bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-brand-700"
                  >
                    View job
                  </Link>
                  <Button
                    variant="ghost"
                    disabled={toggle.isPending}
                    onClick={() => void remove(saved.job.id, saved.job.title)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </TeenShell>
  );
}
